/**
 * The thin structured logger — frontend.md §4.3 / conventions.md §4.
 *
 * Emits JSON lines `{ ts, level, module, correlationId, event, context }`, local-only
 * (PRD §10 — no remote telemetry). In a Tauri build these are forwarded to a Tauri-side
 * log file via a logging command; here the forward is a single seam (`sink`) so the rest
 * of the frontend never touches `console` directly (the ESLint `no-console` rule enforces
 * that — this module is the one sanctioned exception).
 *
 * NEVER LOGGED (conventions.md §4): API keys, the loopback token, `Authorization` header
 * values, full note bodies, chat content. Log note IDs, counts, lengths only. Callers are
 * responsible for not passing secrets in `context`; `redact()` is a coarse safety net.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogLine {
  ts: string;
  level: LogLevel;
  /** The workstream submodule, e.g. 'api.client', 'surface.search'. */
  module: string;
  /** The per-request correlation id, when one applies. */
  correlationId: string | undefined;
  event: string;
  context: Record<string, unknown>;
}

/** Keys that must never appear in a log line's context — a coarse redaction net. */
const FORBIDDEN_KEYS = new Set([
  'token',
  'authorization',
  'api_key',
  'apikey',
  'anthropic_api_key',
  'openai_api_key',
  'joplin_token',
  'body',
  'password',
  'secret',
]);

/** Replace any forbidden-key value with a marker — defends against an accidental secret. */
function redact(context: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(context)) {
    out[k] = FORBIDDEN_KEYS.has(k.toLowerCase()) ? '[redacted]' : v;
  }
  return out;
}

/**
 * Per-module level gate. Verbose in dev, quiet (warn+) in a production build
 * (conventions.md §4 / frontend.md §4.3). `import.meta.env.DEV` is Vite's dev flag.
 */
const MIN_LEVEL: LogLevel = import.meta.env.DEV ? 'debug' : 'warn';
const LEVEL_ORDER: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3 };

/**
 * The single sink. In a Tauri build, swap this for the `invoke('log_line', ...)` command;
 * here it is the one place `console` is used (everything else routes through `logger`).
 */
function sink(line: LogLine): void {
  if (LEVEL_ORDER[line.level] < LEVEL_ORDER[MIN_LEVEL]) return;
  const payload = JSON.stringify(line);
  // eslint-disable-next-line no-console
  (line.level === 'error' ? console.error : console.log)(payload);
}

function emit(
  level: LogLevel,
  module: string,
  event: string,
  context: Record<string, unknown>,
  correlationId?: string,
): void {
  sink({
    ts: new Date().toISOString(),
    level,
    module,
    correlationId,
    event,
    context: redact(context),
  });
}

/** A module-scoped logger — `const log = getLogger('api.client')`. */
export interface Logger {
  debug(event: string, context?: Record<string, unknown>, correlationId?: string): void;
  info(event: string, context?: Record<string, unknown>, correlationId?: string): void;
  warn(event: string, context?: Record<string, unknown>, correlationId?: string): void;
  error(event: string, context?: Record<string, unknown>, correlationId?: string): void;
}

export function getLogger(module: string): Logger {
  return {
    debug: (e, c = {}, cid) => emit('debug', module, e, c, cid),
    info: (e, c = {}, cid) => emit('info', module, e, c, cid),
    warn: (e, c = {}, cid) => emit('warn', module, e, c, cid),
    error: (e, c = {}, cid) => emit('error', module, e, c, cid),
  };
}
